import Image from "next/image";

import { FcPositiveDynamic } from "react-icons/fc";
import { TbChartCovariate } from "react-icons/tb";

import { faBitcoin } from "@fortawesome/free-brands-svg-icons/faBitcoin";
import { faListAlt, faSatelliteDish } from "@fortawesome/free-solid-svg-icons";
import { faChartArea } from "@fortawesome/free-solid-svg-icons/faChartArea";
import { faChartBar } from "@fortawesome/free-solid-svg-icons/faChartBar";
import { faChartLine } from "@fortawesome/free-solid-svg-icons/faChartLine";
import { faCompass } from "@fortawesome/free-solid-svg-icons/faCompass";
import { faDollarSign } from "@fortawesome/free-solid-svg-icons/faDollarSign";
import { faExchangeAlt } from "@fortawesome/free-solid-svg-icons/faExchangeAlt";
import { faInfo } from "@fortawesome/free-solid-svg-icons/faInfo";
import { faUniversalAccess } from "@fortawesome/free-solid-svg-icons/faUniversalAccess";

import FontAwesomeIcon from "components/FontAwesomeIcon";
import { emberAppBaseUrl } from "utils/envVariables";

const allUsers = [
  "isIndividualPass",
  "isInsightProvider",
  "isCorporateOfficer",
  "isIntroducer",
  "isPreviewPass",
  "isClient",
];

export const tools = [
  // Markets
  {
    id: "market-overview",
    title: "Market Overview",
    icon: <FontAwesomeIcon icon={faExchangeAlt} style={{ rotate: "90deg" }} />,
    className: "bg-smart-teal-300 group-hover:bg-[#339b77]",
    description:
      "Global markets performance, gainers and losers and key events",
    href: `${emberAppBaseUrl}/tools/market-overview`,
    type: "markets",
    access: [...allUsers],
    public: true,
  },
  {
    id: "fx-rates",
    title: "FX & Rates",
    icon: <FontAwesomeIcon icon={faDollarSign} />,
    className: "bg-smart-teal-300 group-hover:bg-[#339b77]",
    description: "Real-time FX quotes, forex heatmap and screener",
    href: `${emberAppBaseUrl}/tools/fx-rates`,
    type: "markets",
    access: [...allUsers],
    public: true,
  },
  {
    id: "crypto",
    title: "Crypto",
    icon: <FontAwesomeIcon icon={faBitcoin} />,
    className: "bg-smart-teal-300 group-hover:bg-[#339b77]",
    description:
      "Crypto asset pricing, fundamental data, heatmap, screener and key news",
    href: `${emberAppBaseUrl}/tools/crypto`,
    type: "markets",
    access: [...allUsers],
    public: true,
  },
  {
    id: "heteronomics-inflation-forecasts",
    title: "Heteronomics Inflation Forecasts",
    icon: <FontAwesomeIcon icon={faInfo} />,
    className: "bg-smart-teal-300 group-hover:bg-[#339b77]",
    description: "EU/UK forecasts and proprietary indicators from Heteronomics",
    link: "/tools/heteronomics-inflation-forecasts",
    deniedLink: "/profiles/phil-rush/premium-subscriptions",
    needPremiumAccess: true,
    interfaceSettingKey: "iftEnabled",
    target: "_self",
    enabled: true,
    access: [...allUsers.filter((user) => !["isPreviewPass"].includes(user))],
    paidOnly: true,
    type: "markets",
    activityKey: "tool.view",
  },

  //charting
  {
    id: "grt",
    title: "Smartkarma Graph Ratio (GRT)",
    icon: <TbChartCovariate />,
    className: "bg-smart-teal-300 group-hover:bg-[#339b77]",
    description: "Compare and analyse security price ratios",
    href: `/tools/grt-comparison`,
    type: "charting",
    access: [...allUsers],
  },
  {
    id: "chart-builder",
    title: "Chart Builder",
    icon: <FontAwesomeIcon icon={faChartLine} />,
    className: "bg-smart-teal-300 group-hover:bg-[#0266a5]",
    description:
      "Create simple, beautiful charts that make your Insights stand out",
    href: "http://chartbuilder.smartkarma.com/",
    enabled: true,
    type: "charting",
    rel: "nofollow noreferrer",
    access: [...allUsers],
    activityKey: "tool.view",
  },

  // Event-Driven
  {
    id: "quiddity-northbound-monitor",
    title: "Quiddity Northbound Monitor",
    icon: (
      <Image
        src="https://assets.smartkarma.com/tools/tool-n.svg"
        alt="Quiddity Northbound Monitor"
        width={20}
        height={20}
      />
    ),
    className: "bg-smart-teal-300 group-hover:bg-[#339b77]",
    description: "Monitor China/HK Northbound Liquidity flows",
    link: "/tools/quiddity/north-flow-monitor",
    permission: "canViewQuiddityTools",
    access: ["canViewQuiddityTools"],
    paidOnly: true,
    hideOnRestricted: true,
    type: "event-driven",
  },
  {
    id: "quiddity-southbound-monitor",
    title: "Quiddity Southbound Monitor",
    icon: (
      <Image
        src="https://assets.smartkarma.com/tools/tool-s.svg"
        alt="Quiddity Southbound Monitor"
        width={20}
        height={20}
      />
    ),
    className: "bg-smart-teal-300 group-hover:bg-[#339b77]",
    description: "Monitor China/HK Southbound Liquidity flows",
    link: "/tools/quiddity/south-flow-monitor",
    access: ["canViewQuiddityTools"],
    hideOnRestricted: true,
    paidOnly: true,
    type: "event-driven",
  },
  {
    id: "quiddity-a-h-shares-monitor",
    title: "Quiddity A/H Shares Monitor",
    icon: (
      <Image
        src="https://assets.smartkarma.com/tools/tool-ah.svg"
        alt="Quiddity A/H Shares Monitor"
        width={20}
        height={20}
      />
    ),
    className: "bg-smart-teal-300 group-hover:bg-[#339b77]",
    description: "Track China/HK A/H share Premia and Performance by Sector",
    link: "/tools/quiddity/ah-shares-monitor",
    access: ["canViewQuiddityTools"],
    hideOnRestricted: true,
    paidOnly: true,
    type: "event-driven",
  },

  // Screeners
  {
    id: "smartkarma-smartscore-screener",
    title: "Smartkarma SmartScore Screener",
    icon: (
      <Image
        src="https://assets.smartkarma.com/tools/filter.svg"
        alt="Smartkarma SmartScore Screener"
        width={20}
        height={20}
      />
    ),
    className: "bg-smart-teal-300 group-hover:bg-[#339b77]",
    breadcrumbSvg: "/filter-black.svg",
    description: "Screen for companies on SmartScore Factors",
    link: `/tools/smart-score-screener`,
    permission: "id",
    access: [...allUsers],
    type: "screeners",
  },
  {
    id: "executives-analytics",
    title: "Smartkarma Executives Analytics",
    icon: (
      <Image
        src={require("../assets/user-chart-white.svg")}
        alt="Smartkarma Executives Analytics"
        width={20}
        height={20}
      />
    ),
    className: "bg-smart-teal-300 group-hover:bg-[#339b77]",
    breadcrumbSvg: "/filter-black.svg",
    description:
      "Screen for top executives based on the performance of their trades done on their companies.",
    link: `/tools/executives-analytics`,
    access: [...allUsers],
    public: true,
    type: "screeners",
  },

  {
    id: "relative-value",
    title: "Smartkarma Relative Value",
    icon: <FontAwesomeIcon icon={faChartArea} />,
    className: "bg-smart-teal-300 group-hover:bg-[#339b77]",
    breadcrumbSvg: "/filter-black.svg",
    description:
      "Monitor price ratios of closely correlated pairs such as HoldCos, and Share classes",
    link: `/tools/relative-value`,
    permission: "canViewExecutivesAnalytics",
    access: [...allUsers],
    public: true,
    type: "screeners",
  },
  {
    id: "discovery",
    title: "Smartkarma Discovery",
    icon: <FontAwesomeIcon icon={faCompass} />,
    className: "bg-smart-teal-300 group-hover:bg-[#339b77]",
    description:
      "Most searched Entities with fewest number of Insights published recently",
    href: `${emberAppBaseUrl}/tools/discovery`,
    enabled: true,
    access: [...allUsers],
    type: "screeners",
  },
  {
    id: "peers-comparison",
    title: "Peers Comparison",
    icon: <FontAwesomeIcon icon={faChartBar} />,
    className: "bg-smart-teal-300 group-hover:bg-[#339b77]",
    description: "Compare fundamentals of Peer Companies",
    link: `/tools/peers-comparison`,
    access: [...allUsers.filter((user) => user !== "isPreviewPass")],
    paidOnly: true,
    type: "screeners",
  },
  {
    id: "taiwan-monthly-screener",
    title: "Taiwan Monthly Revenue Monitor",
    icon: (
      <FcPositiveDynamic
        className="[&>g]:!fill-white"
        size={20}
        stroke="white"
        fill="white"
      />
    ),
    className: "bg-smart-teal-300 group-hover:bg-[#339b77]",
    description:
      "Track monthly revenue trends of Taiwanese listed companies with Smartkarma's Actionability Score",
    link: `/tools/taiwan-monthly-screener`,
    type: "screeners",
    access: [...allUsers.filter((user) => user !== "isPreviewPass")],
    permission: "id",
    isNew: true,
    paidOnly: true,
  },
  {
    id: "fund-screener",
    title: "Fund Screener",
    icon: <FontAwesomeIcon icon={faListAlt} />,
    className: "bg-smart-teal-300 group-hover:bg-[#339b77]",
    description: "Screen for Funds & Fund Managers",
    link: `/tools/fund-screener`,
    isNew: true,
    isInternalOnly: false,
    paidOnly: true,
    access: [...allUsers.filter((user) => user !== "isPreviewPass")],
    withBrowserNavigation: true,
    type: "screeners",
  },
  {
    id: "active-situations-monitor",
    title: "Smartkarma Active Situations Monitor",
    icon: <FontAwesomeIcon icon={faSatelliteDish} />,
    className: "bg-smart-teal-300 group-hover:bg-[#339b77]",
    description:
      "Track active event-driven, index rebalance and ECM situations",
    link: `/tools/active-situations-monitor`,
    type: "screeners",
    access: [...allUsers],
    public: true,
    isNew: true,
  },
  {
    id: "smartkarma-indices",
    title: "Smartkarma Indices",
    icon: (
      <Image
        src={require("../assets/stock-exchange.png")}
        alt="Smartkarma Executives Analytics"
        width={32}
        height={32}
      />
    ),
    className: "bg-smart-teal-300 group-hover:bg-[#339b77]",
    description: "Model Portfolios and Thematic Baskets",
    link: `/tools/smartkarma-indices`,
    type: "screeners",
    access: [...allUsers],
    public: true,
    isNew: true,
  },

  // Insight Providers
  {
    id: "payment-analytics",
    title: "Payment Analytics",
    icon: <FontAwesomeIcon icon={faChartBar} />,
    className: "bg-utah-sky group-hover:bg-[#0266a5]",
    description: "Track and measure your performance on Smartkarma",
    href: `${emberAppBaseUrl}/tools/payment-analytics`,
    queryParams: {
      tab: "overview",
      id: null,
    },
    enabled: true,
    permission: "canViewPaymentAnalytics",
    type: "insight-providers",
    access: ["canViewPaymentAnalytics"],
  },

  // Channel Partner
  {
    id: "elysium",
    title: "Elysium",
    icon: <FontAwesomeIcon icon={faUniversalAccess} />,
    className: "bg-glacial-blue group-hover:bg-[#03bbd7]",
    isBeta: true,
    description: "Understand Client consumption, receive Analytics on the go",
    href: `${emberAppBaseUrl}/tools/elysium`,
    enabled: true,
    permission: "canViewElysium",
    type: "channel-partner",
    access: ["canViewElysium"],
  },
];

export type Tool = (typeof tools)[number];
